import React, { useEffect, useRef, useState } from "react";
import { Pause, Play, X, Plus } from "lucide-react";

/**
 * Floating rest timer. Pinned to the bottom of the workout screen.
 * Props:
 *  - seconds: initial duration (from ex.rest)
 *  - onDone: called when timer reaches 0 or is dismissed
 *  - label: exercise name
 */
export default function RestTimer({ seconds, onDone, label }) {
  const [remaining, setRemaining] = useState(seconds);
  const [paused, setPaused] = useState(false);
  const doneCalled = useRef(false);
  // Stash onDone in a ref so the effect doesn't retrigger when the parent re-renders
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    if (paused) return;
    if (remaining <= 0) {
      if (!doneCalled.current) {
        doneCalled.current = true;
        // Small delay so user sees "Go" state briefly
        const t = setTimeout(() => onDoneRef.current && onDoneRef.current(), 600);
        return () => clearTimeout(t);
      }
      return;
    }
    const t = setTimeout(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearTimeout(t);
  }, [remaining, paused]);

  const pct = Math.max(0, Math.min(1, remaining / seconds));
  const min = Math.floor(remaining / 60);
  const sec = remaining % 60;
  const mm = String(min).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  const finished = remaining <= 0;

  return (
    <div
      className="fixed left-0 right-0 z-40 pb-safe"
      style={{ bottom: 76 }}
      data-testid="rest-timer"
    >
      <div className="max-w-md mx-auto px-5">
        <div className={`card p-3 pl-4 flex items-center gap-3 ${finished ? "border-lime" : ""}`} style={{ background: "rgba(10,10,10,0.96)", backdropFilter: "blur(12px)" }}>
          <div className="flex-1 min-w-0">
            <div className="overline">{finished ? "Rest done" : "Rest"} · {label}</div>
            <div className="mt-1 h-1 bg-elev rounded-full overflow-hidden">
              <div className="h-full transition-[width] duration-500 ease-linear" style={{ width: `${pct * 100}%`, background: finished ? "var(--lime)" : "#333" }} />
            </div>
          </div>
          <div className={`font-display text-2xl tabular ${finished ? "text-lime" : ""}`} data-testid="rest-timer-value">{finished ? "GO" : `${mm}:${ss}`}</div>
          <button className="stepper" onClick={() => setRemaining((r) => r + 15)} data-testid="rest-timer-plus" aria-label="Add 15 seconds"><Plus size={16} /></button>
          <button className="stepper" onClick={() => setPaused((p) => !p)} data-testid="rest-timer-toggle" aria-label={paused ? "Resume" : "Pause"}>
            {paused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          <button className="stepper" onClick={onDone} data-testid="rest-timer-close" aria-label="Skip rest">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
